/* The store owns cart access. This module never needs Shopify Admin credentials. */
(function(root, factory){
  const api=factory();if(typeof module==='object'&&module.exports)module.exports=api;
  else root.CBCommerceCore=api;
})(typeof window==='undefined'?globalThis:window, function(){
  'use strict';
  const integer=n=>Number.isSafeInteger(n)&&n>=0;
  const canonical=o=>JSON.stringify(Object.fromEntries(Object.entries(o||{}).sort(([a],[b])=>a.localeCompare(b))));
  const groupKey=item=>JSON.stringify([Number(item.variant_id||item.id),canonical(item.properties),item.selling_plan_allocation?.selling_plan?.id||null]);
  function quantities(cart){const m=new Map();for(const item of cart.items){const key=groupKey(item);m.set(key,(m.get(key)||0)+item.quantity);}return m;}
  function assertPreserved(before,after){const actual=quantities(after);for(const [key,n]of quantities(before))if((actual.get(key)||0)<n)throw new Error('The cart changed in another tab. Please review your cart before checkout.');}
  function validateMaterials(lines,catalog){
    if(!Array.isArray(lines)||!lines.length||lines.length>221)throw new Error('Your bead list is empty or invalid.');
    const seen=new Set(),sizes=new Set();
    return lines.map(line=>{
      const size=catalog.sizes[line.size];
      const color=(size?.colors||catalog.colors).find(c=>c.code===line.code);
      if(size?.palette_version&&line.palette_version!==size.palette_version)throw new Error('Regenerate this pattern with the current bead colors before buying.');
      if(line.owned===true)throw new Error('Remove already-owned colors from the purchase list.');
      if(!color||!size?.enabled||!integer(line.count)||line.count===0||line.count>19200||seen.has(line.code))throw new Error('A color or bead size could not be matched.');
      seen.add(line.code);sizes.add(line.size);if(sizes.size>1)throw new Error('A pattern must use one bead size.');
      const variant=color.variants[line.size],quantity=Math.ceil(line.count/size.pack_count);
      if(!variant||Number(line.id)!==variant.id||line.sku!==variant.sku||line.quantity!==quantity||line.pack_count!==size.pack_count)throw new Error('The bead list no longer matches the store catalog.');
      return {code:line.code,hex:color.hex,count:line.count,size:line.size,pack_count:size.pack_count,quantity,spare:quantity*size.pack_count-line.count,handle:color.handle,
        ...(size.palette_version?{palette_version:size.palette_version}:{}),...variant};
    }).sort((a,b)=>a.id-b.id);
  }
  async function digest(value){const bytes=await globalThis.crypto.subtle.digest('SHA-256',new TextEncoder().encode(value));return Array.from(new Uint8Array(bytes),b=>b.toString(16).padStart(2,'0')).join('');}
  class Cart {
    constructor({catalog,fetch:fetcher,root='/',storage,lock,preview=false}){
      this.catalog=catalog;this.fetch=fetcher;this.root=root.endsWith('/')?root:root+'/';this.storage=storage;
      this.lock=lock||((fn)=>fn());this.preview=preview;this.busy=false;
      if(!/^\/(?:[a-zA-Z0-9-]+\/)*$/.test(this.root))throw new Error('Invalid store route.');
    }
    async json(path,options={}){
      const r=await this.fetch(this.root+path,{credentials:'same-origin',cache:'no-store',...options});
      if(!r.ok){let error;try{error=await r.json();}catch{}throw new Error(typeof error?.description==='string'?error.description:'The store could not be reached. Please try again.');}
      const data=await r.json();
      if(path==='cart.js'){if(!Array.isArray(data.items)||!/^\w{3}$/.test(data.currency))throw new Error('The store returned an invalid cart.');this.lastCart=data;}
      return data;
    }
    async context(projectId,input){
      if(!/^[A-Za-z0-9_-]{12,64}$/.test(projectId))throw new Error('Invalid pattern reference.');
      const lines=validateMaterials(input,this.catalog);
      const fingerprint=await digest(JSON.stringify([this.catalog.purchase?.version,projectId,lines.map(p=>p.palette_version?[p.id,p.count,p.quantity,p.pack_count,p.palette_version]:[p.id,p.count,p.quantity,p.pack_count])]));
      return {projectId,lines,fingerprint,intent:'cb-'+fingerprint.slice(0,32)};
    }
    own(cart,ctx,id){return cart.items.filter(v=>Number(v.variant_id||v.id)===id&&v.properties?._cb_list===ctx.intent).reduce((n,v)=>n+v.quantity,0);}
    complete(cart,ctx){return ctx.lines.every(p=>this.own(cart,ctx,p.id)>=p.quantity);}
    readState(intent){try{return JSON.parse(this.storage.getItem(intent)||'null');}catch{return {state:'uncertain'};}}
    writeState(intent,value){this.storage.setItem(intent,JSON.stringify(value));}
    async liveVariants(lines){
      const products=new Map();
      const handles=[...new Set(lines.map(line=>line.handle))];
      for(let i=0;i<handles.length;i+=6){
        await Promise.all(handles.slice(i,i+6).map(async handle=>{
          const product=await this.json('products/'+encodeURIComponent(handle)+'.js');
          if(product?.handle!==handle||!Array.isArray(product.variants))throw new Error('A bead color could not be verified with the store.');
          products.set(handle,product);
        }));
      }
      return new Map(lines.map(line=>{
        const variant=products.get(line.handle)?.variants.find(item=>Number(item.id)===line.id);
        if(!variant||variant.sku!==line.sku||!integer(variant.price)||typeof variant.available!=='boolean')
          throw new Error(`${line.code} no longer matches the store catalog. Regenerate the bead list before buying.`);
        return [line.id,variant];
      }));
    }
    estimate(ctx,cart,liveVariants){
      const offer=this.catalog.purchase;
      if(!offer||!/^\w{3}$/.test(cart.currency)||!(liveVariants instanceof Map))throw new Error('The store could not verify bead prices.');
      const offerPending=!offer.shopify_binding_ready&&!this.preview;
      const lines=ctx.lines.map(line=>{
        const inList=this.own(cart,ctx,line.id),missing=Math.max(0,line.quantity-inList);
        const variant=liveVariants.get(line.id);
        return {...line,price:variant.price,missing,inList,available:variant.available,stock:null,
          status:missing===0?'In your cart':variant.available?'Ready to add':'Currently unavailable'};
      });
      const cartNeedsReview=cart.items.some(i=>i.properties?._cb_project===ctx.projectId&&i.properties?._cb_list!==ctx.intent);
      return {lines,currency:cart.currency,total:lines.reduce((n,p)=>n+p.price*p.quantity,0),
        addTotal:lines.reduce((n,p)=>n+p.price*p.missing,0),canAdd:!offerPending&&!cartNeedsReview&&lines.every(p=>p.missing===0||p.available),cartNeedsReview,
        fingerprint:ctx.fingerprint,intent:ctx.intent,alreadyComplete:this.complete(cart,ctx),
        preview:this.preview,offerPending,estimated:false};
    }
    async quote(projectId,input){
      const ctx=await this.context(projectId,input);
      const [cart,variants]=await Promise.all([this.json('cart.js'),this.liveVariants(ctx.lines)]);
      return this.estimate(ctx,cart,variants);
    }
    async add(projectId,input,expected){
      if(this.catalog.purchase&&!this.catalog.purchase.shopify_binding_ready&&!this.preview)
        throw new Error('The 500-bead packs are not available for checkout yet.');
      if(this.busy)throw new Error('Your colors are already being added.');this.busy=true;
      try{return await this.lock(async()=>{
        const ctx=await this.context(projectId,input),before=await this.json('cart.js');
        if(this.complete(before,ctx)){this.writeState(ctx.intent,{state:'complete'});return {complete:true,preview:this.preview};}
        const state=this.readState(ctx.intent);
        if(state?.state==='pending'||state?.state==='uncertain')throw new Error('A previous add request could not be confirmed. Please review your cart; automatic retry is paused to avoid duplicate packs.');
        const quote=this.estimate(ctx,before,await this.liveVariants(ctx.lines));
        if(quote.cartNeedsReview)throw new Error('This pattern already has items in your cart. Review those items before adding a changed list.');
        if(!quote.canAdd)throw new Error('Review your bead list before adding it.');
        if(quote.fingerprint!==expected?.fingerprint||quote.currency!==expected.currency||quote.total!==expected.total)throw new Error('The bead list or price changed. Review the updated total and try again.');
        const items=quote.lines.filter(p=>p.missing>0).map(p=>({id:p.id,quantity:p.missing,properties:{
          _cb_list:ctx.intent,_cb_project:ctx.projectId,'Pattern color':p.code,'Beads needed':String(p.count)}}));
        if(!items.length)return {complete:true,preview:this.preview};
        this.writeState(ctx.intent,{state:'pending',at:Date.now()});
        let response,networkError;
        try{response=await this.fetch(this.root+'cart/add.js',{method:'POST',credentials:'same-origin',cache:'no-store',
          headers:{'Content-Type':'application/json'},body:JSON.stringify({items})});}
        catch(e){networkError=e;}
        let after;
        try{after=await this.json('cart.js');assertPreserved(before,after);}
        catch(e){this.writeState(ctx.intent,{state:'uncertain'});throw e;}
        if(this.complete(after,ctx)){
          this.writeState(ctx.intent,{state:'complete'});
          return {complete:true,preview:this.preview,itemCount:after.item_count};
        }
        const definitiveFailure=response&&response.status>=400&&response.status<500;
        this.writeState(ctx.intent,{state:definitiveFailure?'partial':'uncertain'});
        if(networkError||!definitiveFailure)throw new Error('The add request could not be fully confirmed. Review your cart before retrying.');
        return {complete:false,preview:this.preview,message:'Only part of the bead list was added. Your existing cart items are kept. Review your cart and retry to add only the missing packs.'};
      });}finally{this.busy=false;}
    }
    async verify(projectId,input){const ctx=await this.context(projectId,input);const cart=await this.json('cart.js');return this.complete(cart,ctx);}
  }
  return {Cart,validateMaterials,assertPreserved};
});
